import React, { useMemo, useState } from 'react';
import { differenceInDays, parseISO, startOfToday } from 'date-fns';
import { Spinner } from '@heroui/react';
import { Icons } from '~/components/icons';
import { ROLES } from '~/constant';
import { API_ENDPOINTS } from '~/constant/api-endpoints';
import { useCrud } from '~/hooks';
import { cn } from '~/lib/utils';
import { formatCurrency } from '~/utils/formater';

interface RenewPackageModalProps {
	user: any;
	onClose?: () => void;
	onProceedPayment: (data: {
		idProduct?: number;
		productCode?: string;
		billingCycle: '6' | '12';
		totalAmount: number;
		packageAmount: number;
		extraStorage?: number;
		extraStorageAmount?: number;
	}) => void;
}

// Chuẩn hoá giá tiền nếu backend trả đơn vị theo nghìn đồng (ví dụ: 37500 -> 37.500.000)
const normalizeAmount = (val: any) => {
	if (!val) return 0;
	const n = Number(val);
	if (Number.isNaN(n)) return 0;
	if (n > 0 && n < 1000000) {
		return n * 1000;
	}
	return n;
};

export const RenewPackageModal: React.FC<RenewPackageModalProps> = ({
	user,
	onClose,
	onProceedPayment,
}) => {
	const [billingCycle, setBillingCycle] = useState<'6' | '12'>('12');
	const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
	// Gọi API renew info
	const { getOne: getRenewInfo } = useCrud(
		[API_ENDPOINTS.payment.renewInfo],
		{ endpoint: ROLES.ROOT },
		{ staleTime: 0 },
	);
	const { data: renewInfoResponse, isFetching: isLoading }: any = getRenewInfo();

	// Parse dữ liệu từ API response theo schema người dùng cung cấp
	const info = useMemo(() => {
		const raw = renewInfoResponse?.data ?? renewInfoResponse;
		const listAddon: Array<{
			id?: number;
			storage?: number;
			amount?: number;
			amount_6_month?: number;
			[key: string]: any;
		}> = Array.isArray(raw?.list_addon) ? raw.list_addon : [];

		const storageAddon = Number(
			raw?.storage_addon ??
			listAddon.reduce((sum, item) => sum + Number(item.storage || 0), 0),
		);
		const storageAddonCount = Number(
			raw?.storage_addon_count ?? listAddon.length,
		);

		const storageAmount6Month = Number(
			raw?.storage_amount_6_month ??
			listAddon.reduce(
				(sum, item) => sum + Number(item.amount_6_month || 0),
				0,
			),
		);
		const storageAmount12Month = Number(
			raw?.storage_amount_12_month ??
			listAddon.reduce((sum, item) => sum + Number(item.amount || 0), 0),
		);

		const amount6Month = Number(raw?.amount_6_month || 0);
		const amount12Month = Number(raw?.amount || 0);

		return {
			idProduct: raw?.id_product ?? user?.id_product,
			productCode: (
				raw?.product_code ||
				user?.product_code ||
				'STARTER'
			).toUpperCase(),
			amount6Month,
			amount12Month,
			storageAddon,
			storageAddonCount,
			storageAmount6Month,
			storageAmount12Month,
			listAddon,
			expiredDate: raw?.expired_date || user?.expired_date || null,
			graceDaysRemain: raw?.grace_days_remain ?? 0,
		};
	}, [renewInfoResponse, user]);

	const productCode = info?.productCode || 'STARTER';
	const price6Month = info?.amount6Month || 0;
	const price12Month = info?.amount12Month || 0;

	const monthlyPrice6 = Math.round(price6Month / 6);
	const monthlyPrice12 = Math.round(price12Month / 12);

	// Chi phí gói theo chu kỳ đang chọn
	const currentPackageFee = billingCycle === '6' ? price6Month : price12Month;

	// Chi phí dung lượng theo chu kỳ đang chọn
	const storageAddon = info?.storageAddon || 0;
	const storageAddonCount =
		info?.storageAddonCount || info?.listAddon?.length || 0;
	const currentStorageFee =
		billingCycle === '6'
			? info?.storageAmount6Month || 0
			: info?.storageAmount12Month || 0;
	const monthlyStorageFee = Math.round(
		currentStorageFee / (billingCycle === '6' ? 6 : 12),
	);

	// Tổng thanh toán
	const totalAmount = currentPackageFee + currentStorageFee;

	// Kiểm tra trạng thái đã hết hạn
	const isExpired = useMemo(() => {
		if (info?.graceDaysRemain && info.graceDaysRemain > 0) return true;
		if (!info?.expiredDate) return true;
		try {
			const expiry = parseISO(info.expiredDate);
			const today = startOfToday();
			return differenceInDays(expiry, today) <= 0;
		} catch {
			return true;
		}
	}, [info?.graceDaysRemain, info?.expiredDate]);

	const handleProceed = () => {
		onProceedPayment({
			idProduct: info?.idProduct || user?.id_product,
			productCode,
			billingCycle,
			totalAmount,
			packageAmount: currentPackageFee,
			extraStorage: storageAddon,
			extraStorageAmount: currentStorageFee,
		});
	};

	return (
		<div className="bg-white dark:bg-slate-900 rounded-3xl overflow-hidden p-5 sm:p-6 relative max-w-xl w-full mx-auto shadow-2xl border border-slate-100 dark:border-slate-800 max-h-[92vh] overflow-y-auto">
			{/* Close button */}
			{!user?.is_login_locked &&
				<button
					onClick={onClose}
					className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-white p-1 rounded-full transition-colors cursor-pointer"
					aria-label="Đóng"
				>
					<Icons.close size={20} />
				</button>
			}

			{isLoading && !info ? (
				<div className="flex flex-col items-center justify-center py-20 text-gray-500 gap-3">
					<Spinner size="lg" color="warning" />
					<span className="text-sm font-medium">Đang tải thông tin gói dịch vụ...</span>
				</div>
			) : (
				<div>
					{/* Header */}
					<div className="flex items-center gap-2.5 mb-4 pr-8">
						<div className="size-8 rounded-full bg-orange-100 dark:bg-orange-950/40 flex items-center justify-center shrink-0 text-[#f95721]">
							<Icons.rotateCcw size={16} className="text-[#f95721] stroke-[2.4]" />
						</div>
						<h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
							Gia hạn gói {productCode}
						</h3>
						<span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200/60 dark:bg-red-950/30 dark:text-red-400 dark:border-red-900/40">
							<div className="size-3.5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px] font-bold">
								!
							</div>
							<span>{isExpired ? 'Đã hết hạn' : 'Sắp hết hạn'}</span>
						</span>
					</div>

					{/* Chọn kỳ hạn gia hạn */}
					<div className="mb-4">
						<label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">
							Chọn kỳ hạn gia hạn
						</label>
						<div className="grid grid-cols-2 gap-3">
							{/* 6 tháng */}
							<div
								onClick={() => setBillingCycle('6')}
								className={cn(
									'rounded-2xl p-3 border-2 transition-all cursor-pointer relative',
									billingCycle === '6'
										? 'border-[#f95721] bg-[#fff8f5] dark:bg-orange-950/20'
										: 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-gray-300',
								)}
							>
								<div className="flex items-start gap-2.5">
									<div
										className={cn(
											'size-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5 transition-all',
											billingCycle === '6'
												? 'border-[#f95721] bg-white'
												: 'border-gray-300 dark:border-slate-600',
										)}
									>
										{billingCycle === '6' && (
											<div className="size-2 rounded-full bg-[#f95721]" />
										)}
									</div>
									<div>
										<p className="font-bold text-xs text-gray-900 dark:text-white">
											6 tháng
										</p>
										<p className="text-sm sm:text-base font-extrabold text-red-600 dark:text-red-500 mt-0.5">
											{formatCurrency(price6Month).replace('₫', '').trim()}đ
										</p>
										<p className="text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400">
											({formatCurrency(monthlyPrice6).replace('₫', '').trim()}đ/tháng)
										</p>
									</div>
								</div>
							</div>

							{/* 12 tháng */}
							<div
								onClick={() => setBillingCycle('12')}
								className={cn(
									'rounded-2xl p-3 border-2 transition-all cursor-pointer relative',
									billingCycle === '12'
										? 'border-[#f95721] bg-[#fff8f5] dark:bg-orange-950/20'
										: 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-gray-300',
								)}
							>
								<span className="absolute top-2 right-2 bg-[#eaf8ef] dark:bg-emerald-950/40 text-[#16a34a] dark:text-emerald-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-emerald-200/60 dark:border-emerald-800">
									Tiết kiệm 20%
								</span>
								<div className="flex items-start gap-2.5">
									<div
										className={cn(
											'size-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5 transition-all',
											billingCycle === '12'
												? 'border-[#f95721] bg-white'
												: 'border-gray-300 dark:border-slate-600',
										)}
									>
										{billingCycle === '12' && (
											<div className="size-2 rounded-full bg-[#f95721]" />
										)}
									</div>
									<div>
										<p className="font-bold text-xs text-gray-900 dark:text-white">
											12 tháng
										</p>
										<p className="text-sm sm:text-base font-extrabold text-gray-900 dark:text-white mt-0.5">
											{formatCurrency(price12Month).replace('₫', '').trim()}đ
										</p>
										<p className="text-[10px] sm:text-[11px] text-gray-500 dark:text-gray-400">
											({formatCurrency(monthlyPrice12).replace('₫', '').trim()}đ/tháng)
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Dung lượng mở rộng */}
					{storageAddon > 0 && (
						<div className="border border-gray-100 dark:border-slate-800 bg-[#fafcff] dark:bg-slate-800/40 rounded-2xl p-3.5 mb-4">
							{/* Header khối dung lượng */}
							<div className="flex items-start justify-between gap-2 mb-3">
								<div className="flex items-start gap-2.5">
									<div className="size-8 rounded-lg bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center shrink-0 text-[#f95721] mt-0.5">
										<Icons.database size={18} className="text-[#f95721]" />
									</div>
									<div>
										<h4 className="font-bold text-sm text-gray-900 dark:text-white">
											Dung lượng mở rộng
										</h4>
										<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
											Tất cả dung lượng đã mua thêm sẽ được gia hạn cùng kỳ hạn của gói dịch vụ.
										</p>
									</div>
								</div>
								<span className="shrink-0 bg-[#eaf8ef] dark:bg-emerald-950/40 text-[#16a34a] dark:text-emerald-400 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-emerald-200/60 dark:border-emerald-800">
									Tự động gia hạn
								</span>
							</div>

							{/* 2 cột tổng quan */}
							<div className="grid grid-cols-2 gap-3 py-2 border-t border-gray-100 dark:border-slate-700/60 mb-2.5">
								<div>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										Tổng dung lượng mua thêm
									</p>
									<p className="text-xl font-extrabold text-gray-900 dark:text-white mt-0.5">
										{storageAddon}GB
									</p>
								</div>
								<div>
									<p className="text-xs text-gray-500 dark:text-gray-400">
										Phí duy trì {billingCycle} tháng
									</p>
									<p className="text-xl font-extrabold text-red-600 dark:text-red-500 mt-0.5">
										{formatCurrency(currentStorageFee).replace('₫', '').trim()}đ
									</p>
									<p className="text-[11px] text-gray-500 dark:text-gray-400">
										({formatCurrency(monthlyStorageFee).replace('₫', '').trim()}đ/tháng)
									</p>
								</div>
							</div>

							{/* Accordion Xem chi tiết */}
							{info.listAddon.length > 0 && (
								<div>
									<button
										type="button"
										onClick={() => setIsDetailOpen(!isDetailOpen)}
										className="w-full flex items-center justify-between p-2 rounded-xl bg-[#fff8f5] dark:bg-orange-950/20 text-[#f95721] cursor-pointer hover:bg-orange-100/60 transition-colors"
									>
										<div className="flex items-center gap-2 text-xs font-bold">
											<Icons.list size={16} />
											<span>
												Xem chi tiết {storageAddonCount || info.listAddon.length} lần mua ({storageAddon}GB)
											</span>
										</div>
										{isDetailOpen ? (
											<Icons.chevronUp size={16} />
										) : (
											<Icons.chevronDown size={16} />
										)}
									</button>

									{isDetailOpen && (
										<div className="mt-2.5 space-y-2 px-1 text-xs">
											{info.listAddon.map((item, idx) => {
												const itemFee =
													billingCycle === '6'
														? Number(item.amount_6_month || 0)
														: Number(item.amount || 0);
												return (
													<div
														key={item.id || idx}
														className="flex items-center justify-between py-1 text-gray-700 dark:text-gray-300"
													>
														<div className="flex items-center gap-8">
															<span className="text-gray-500">Lần mua {idx + 1}</span>
															<span className="font-semibold text-gray-900 dark:text-white">
																{item.storage} GB
															</span>
														</div>
														<span className="font-bold text-gray-900 dark:text-white">
															{formatCurrency(itemFee).replace('₫', '').trim()}đ
														</span>
													</div>
												);
											})}
										</div>
									)}
								</div>
							)}
						</div>
					)}

					{/* Chi tiết thanh toán */}
					<div className="bg-[#f8fafc] dark:bg-slate-800/40 rounded-2xl p-4 border border-slate-100 dark:border-slate-800 mb-4">
						<div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-slate-700 mb-3">
							<span className="font-bold text-sm text-gray-900 dark:text-white">
								Chi tiết thanh toán
							</span>
							<span className="text-xs text-gray-500 dark:text-gray-400">
								Kỳ hạn {billingCycle} tháng
							</span>
						</div>

						<div className="space-y-2 text-xs">
							<div className="flex items-center justify-between text-gray-600 dark:text-gray-300">
								<span>Gia hạn gói {productCode}</span>
								<span className="font-bold text-gray-900 dark:text-white text-sm">
									{formatCurrency(currentPackageFee).replace('₫', '').trim()}đ
								</span>
							</div>
							{storageAddon > 0 && (
								<div className="flex items-center justify-between text-gray-600 dark:text-gray-300">
									<span>Duy trì dung lượng mở rộng ({storageAddon}GB)</span>
									<span className="font-bold text-gray-900 dark:text-white text-sm">
										{formatCurrency(currentStorageFee).replace('₫', '').trim()}đ
									</span>
								</div>
							)}
							<div className="pt-2 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between">
								<span className="font-bold text-sm text-gray-900 dark:text-white">
									Tổng thanh toán
								</span>
								<span className="text-xl font-black text-red-600 dark:text-red-500 tabular-nums">
									{formatCurrency(totalAmount).replace('₫', '').trim()}đ
								</span>
							</div>
						</div>
					</div>

					{/* CTA Button */}
					<button
						type="button"
						onClick={handleProceed}
						className="w-full bg-linear-to-r from-[#f95721] to-[#ea580c] hover:brightness-105 active:scale-[0.99] text-white font-bold py-3.5 px-4 rounded-xl transition-all duration-200 text-sm shadow-md cursor-pointer flex items-center justify-center gap-2"
					>
						<Icons.scanQrCode size={18} />
						<span>Gia hạn ngay</span>
						<Icons.arrowRight size={16} />
					</button>
				</div>
			)}
		</div>
	);
};
